import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

import { getMenu } from '@/services/client';
import { useCart } from '@/store/cart.store';
import DishCard from '@/components/DishCard';

// Высота области = viewport − BottomNav(~4.25rem контента) − iOS home indicator.
// Родительский <main> имеет pb-4 — компенсируем его -mb-4 на корне страницы,
// чтобы меню вплотную подходило к нижней навигации без пустой зоны.
const PAGER_HEIGHT = 'calc(100dvh - 4.25rem - env(safe-area-inset-bottom, 0px))';

export default function Menu() {
  const navigate = useNavigate();
  const estId = useCart((s) => s.establishmentId);
  const add = useCart((s) => s.add);

  const { data, isLoading } = useQuery({
    queryKey: ['client/menu', estId],
    queryFn: () => getMenu(estId!),
    enabled: !!estId,
  });

  const categories = useMemo(
    () => (data?.categories ?? []).filter((c) => c.dishes.length > 0),
    [data],
  );

  const [activeIdx, setActiveIdx] = useState(0);
  const pagerRef = useRef<HTMLDivElement | null>(null);
  const chipsBarRef = useRef<HTMLDivElement | null>(null);
  const chipRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const settleRef = useRef<number | null>(null);

  const activeCat = categories[activeIdx]?.id ?? null;

  useEffect(() => {
    setActiveIdx(0);
    const el = pagerRef.current;
    if (el) el.scrollLeft = 0;
  }, [categories]);

  // Определяем активный слайд по окончанию скролла (snap settle), а не в процессе.
  // Без этого IntersectionObserver/onScroll во время snap-отката дают «мигание»
  // между соседними категориями (пицца → салаты → пицца).
  const onPagerScroll = () => {
    if (settleRef.current) window.clearTimeout(settleRef.current);
    settleRef.current = window.setTimeout(() => {
      settleRef.current = null;
      const el = pagerRef.current;
      if (!el) return;
      const w = el.clientWidth || 1;
      const idx = Math.round(el.scrollLeft / w);
      if (idx >= 0 && idx < categories.length) {
        setActiveIdx((prev) => (prev === idx ? prev : idx));
      }
    }, 110);
  };

  useEffect(() => () => {
    if (settleRef.current) window.clearTimeout(settleRef.current);
  }, []);

  // Активный индекс обновляется только после окончания свайпа, поэтому
  // центрируем чипс сразу. Проверяем порог — если уже близко к центру, не трогаем.
  useEffect(() => {
    if (!activeCat) return;
    const chip = chipRefs.current[activeCat];
    const bar = chipsBarRef.current;
    if (!chip || !bar) return;
    const target = chip.offsetLeft - bar.clientWidth / 2 + chip.clientWidth / 2;
    const clamped = Math.max(0, Math.min(bar.scrollWidth - bar.clientWidth, target));
    if (Math.abs(clamped - bar.scrollLeft) < 24) return;
    bar.scrollTo({ left: clamped, behavior: 'smooth' });
  }, [activeCat]);

  const goToIdx = (idx: number) => {
    const el = pagerRef.current;
    if (!el) return;
    el.scrollTo({ left: idx * el.clientWidth, behavior: 'smooth' });
    setActiveIdx(idx);
  };

  if (!estId) return <div className="p-6 text-center text-slate-400">Загрузка заведения…</div>;
  if (isLoading) return <div className="p-6 text-center">Загрузка меню…</div>;
  if (!data) return null;

  return (
    <div className="flex flex-col -mb-4" style={{ height: PAGER_HEIGHT }}>
      <div className="shrink-0 px-4 pt-4 pb-2">
        <h1 className="text-2xl font-semibold truncate">{data.establishment.name}</h1>
      </div>

      <div className="shrink-0 bg-slate-50/90 backdrop-blur">
        <div
          ref={chipsBarRef}
          className="flex gap-2 overflow-x-auto no-scrollbar px-4 py-2"
        >
          {categories.map((c, i) => (
            <button
              key={c.id}
              ref={(el) => { chipRefs.current[c.id] = el; }}
              onClick={() => goToIdx(i)}
              className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap border transition-colors ${
                i === activeIdx
                  ? 'bg-brand-600 text-white border-brand-600'
                  : 'bg-white text-slate-700 border-slate-200'
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>
      </div>

      {categories.length === 0 ? (
        <div className="flex-1 flex items-center justify-center text-slate-400">
          Меню пустое
        </div>
      ) : (
        <div
          ref={pagerRef}
          onScroll={onPagerScroll}
          className="flex-1 flex overflow-x-auto overflow-y-hidden snap-x snap-mandatory no-scrollbar overscroll-contain"
          style={{ scrollbarWidth: 'none' }}
        >
          {categories.map((c) => (
            <section
              key={c.id}
              data-cat-id={c.id}
              className="snap-center snap-always shrink-0 w-full h-full overflow-y-auto overscroll-contain no-scrollbar px-4 pt-3 pb-6"
            >
              <div className="grid grid-cols-2 gap-3">
                {c.dishes.map((d) => (
                  <DishCard
                    key={d.id}
                    dish={d}
                    onOpen={() => navigate(`/menu/${d.id}`)}
                    onQuickAdd={() => {
                      add({
                        dishId: d.id,
                        name: d.name,
                        price: d.price,
                        photoUrl: d.photoUrl,
                        modifiers: [],
                      });
                      toast.success(`${d.name} добавлен`);
                    }}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

